import { useEffect, useState } from 'react';

/**
 * How many pixels the on-screen keyboard covers at the bottom of the layout
 * viewport (iPad and phone Safari overlay fixed elements instead of resizing
 * the page). 0 on desktop or with no keyboard up.
 *
 * Reads window.visualViewport on its own resize and scroll events, not a
 * ResizeObserver: what we move never changes the visual viewport, so there is
 * no feedback loop, and state only changes when the inset really changes.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => {
      // Pinch-zoom shrinks the visual viewport too; only an unzoomed viewport
      // that got shorter means a keyboard
      const zoomed = vv.scale > 1.01;
      const next = zoomed ? 0 : Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      // Ignore sub-pixel jitter and the URL bar's small changes
      setInset((prev) => (Math.abs(next - prev) > 1 ? (next < 60 ? 0 : next) : prev));
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return inset;
}
